<template>
  <span class="info-bubble-wrap">
    <!-- Trigger icon -->
    <button
      ref="trigger"
      class="info-icon"
      :style="{ color: iconColor }"
      type="button"
      aria-haspopup="dialog"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="id"
      :title="title"
      @click.stop="toggle"
      @keydown.esc="open = false"
    >
      <i class="fa-solid fa-circle-info" data-testid="info-button"></i>
    </button>

    <!-- Bubble (fixed, viewport-clamped) -->
    <div
      v-if="open"
      :id="id"
      ref="bubble"
      class="info-bubble"
      role="dialog"
      :style="bubbleStyle"
      @click.stop
    >
      <div class="bubble-inner">
        <div
          class="bubble-content"
          data-testid="info-bubble-text"
          v-html="text"
        ></div>
        <button
          class="bubble-close"
          type="button"
          aria-label="Close"
          @click="open = false"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
      <span
        ref="arrow"
        class="bubble-arrow"
        :style="arrowStyle"
        aria-hidden="true"
      ></span>
    </div>
  </span>
</template>

<script>
export default {
  name: "InfoBubble",
  props: {
    text: { type: String, default: "" },
    title: { type: String, default: "More info" },
    iconColor: { type: String, default: "#f48831" },
  },
  data() {
    return {
      open: false,
      bubbleStyle: {},
      arrowStyle: {},
      id: `info-bubble-${Math.random()
        .toString(36)
        .slice(2)}`,
    };
  },
  mounted() {
    document.addEventListener("click", this.onOutside);
    window.addEventListener("resize", this.reposition, { passive: true });
    window.addEventListener("scroll", this.scroll, { passive: true });
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onOutside);
    window.removeEventListener("resize", this.reposition);
    window.removeEventListener("scroll", this.scroll);
  },
  methods: {
    scroll() {
      if (!this.open) return;

      this.toggle();
    },
    toggle() {
      this.open = !this.open;
      if (!this.open) return;

      // 1) Give the bubble a fixed, hidden, off-screen layout to measure accurately.
      this.$nextTick(() => {
        this.bubbleStyle = {
          position: "fixed",
          left: "-9999px",
          top: "0px",
          visibility: "hidden",
        };

        // 2) Wait a frame so styles apply, then measure & place.
        requestAnimationFrame(() => {
          this.reposition(true); // first=true
          // 3) Observe future size changes (slot/text wraps, fonts load).
          if (window.ResizeObserver && this.$refs.bubble && !this._ro) {
            this._ro = new ResizeObserver(() => this.reposition());
            this._ro.observe(this.$refs.bubble);
          }
        });
      });
    },
    onOutside(e) {
      if (!this.$el.contains(e.target)) this.open = false;
    },
    reposition() {
      if (!this.open) return;
      const trigger = this.$refs.trigger;
      const bubble = this.$refs.bubble;
      if (!trigger || !bubble) return;

      const vw = window.innerWidth;
      const margin = 8;
      const sidePad = vw <= 768 ? 22 : 8;

      // Clamp width to viewport
      const desiredMax = 360;
      const maxWidth = Math.min(desiredMax, vw - sidePad * 2);
      bubble.style.maxWidth = `${maxWidth}px`;

      const bubbleRect = bubble.getBoundingClientRect();
      const trigRect = trigger.getBoundingClientRect();
      const bubbleW = Math.min(bubbleRect.width || maxWidth, maxWidth);

      // Always place bubble BELOW the icon
      const top = trigRect.bottom + margin;

      // Center horizontally, clamp to screen
      const iconCenterX = trigRect.left + trigRect.width / 2;
      let left = iconCenterX - bubbleW / 2;
      left = Math.max(sidePad, Math.min(left, vw - bubbleW - sidePad));

      // Arrow position inside bubble
      const arrowSize = 10;
      const arrowHalf = arrowSize / 2;
      let arrowLeft = iconCenterX - left - arrowHalf;
      arrowLeft = Math.max(12, Math.min(arrowLeft, bubbleW - 12 - arrowSize));

      this.bubbleStyle = {
        position: "fixed",
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
      };

      this.arrowStyle = {
        top: `-${arrowHalf}px`,
        left: `${Math.round(arrowLeft)}px`,
      };
    },
  },
};
</script>

<style scoped>
.info-bubble-wrap {
  position: relative;
  display: inline-block;
  vertical-align: middle;
}

/* Info icon */
.info-icon {
  width: 20px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}
.info-icon .fa-circle-info {
  font-size: 18px;
  line-height: 1;
}

/* Bubble */
.info-bubble {
  z-index: 1000;
  background: #3f4549;
  color: #fff;
  padding: 12px 14px 14px;
  border-radius: 10px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.25);
  font-size: 14px;
  line-height: 1.4;
  white-space: normal;
  word-wrap: break-word;
}

/* Mobile tweak */
@media (max-width: 968px) {
  .info-bubble {
    max-width: calc(90vw - 8px);
  }
}

/* Inner layout: text | close */
.bubble-inner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  @media (max-width: 768px) {
    gap: 4px;
  }
}
.bubble-content {
  flex: 1;
  min-width: 300px;
  text-align: left;
}
.bubble-close {
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
}
.bubble-close .fa-xmark {
  pointer-events: none;
}

/* Arrow */
.bubble-arrow {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #3f4549;
  transform: rotate(45deg);
  box-shadow: -1px -1px 1px rgba(0, 0, 0, 0.08);
}

/* Smallest screen shrink */
@media (max-width: 400px) {
  .bubble-content {
    min-width: 220px;
  }
}
</style>
